
import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FAQSection from '../components/FAQSection';
import { HelpCircle, MessageCircleQuestion, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQ = () => {
  const faqItems = [
    {
      question: "How does the application process work?",
      answer: (
        <>
          <p className="mb-2">Our application process consists of four simple steps:</p>
          <ol className="list-decimal ml-5 space-y-2">
            <li>Sign up with your basic information and verify your phone number</li>
            <li>Choose a position from our list of job openings and upload your resume</li>
            <li>Complete a role-specific assessment to showcase your skills</li>
            <li>Record a short video introduction to help us get to know you better</li>
          </ol>
          <p className="mt-2">After completing these steps, our team will review your application and get back to you promptly.</p>
        </>
      )
    },
    {
      question: "How does the AI-powered job search work?",
      answer: "Our job search uses advanced AI technology to understand what you're looking for, even with typos or partial matches. It can recognize similar words and variations, much like Google's search engine. This means you can find relevant positions even if your search terms aren't exact matches to the job titles or descriptions."
    },
    {
      question: "What happens after I submit my application?",
      answer: "After submission, our AI system analyzes your resume and assessment responses. Employers can then review your application, including your video introduction. You'll receive updates on your application status through our portal and via email. If there's interest in your profile, you may be contacted for further interviews or next steps."
    },
    {
      question: "How is my data protected?",
      answer: "We take data security seriously. All personal information is encrypted and stored securely. Your resume and video introductions are only accessible to authorized employers reviewing your application. We comply with data protection regulations and never sell your information to third parties. You can request deletion of your data at any time through your account settings."
    },
    {
      question: "Can I apply for multiple positions?",
      answer: "Yes, you can apply for multiple positions using the same account. Each application may require specific assessments relevant to that role. Our system will keep track of all your applications, and you can view their status in your dashboard."
    },
    {
      question: "How do I check the status of my application?",
      answer: "You can check your application status by logging into your account and visiting the \"Application Status\" page. This dashboard shows all your active applications and their current status - from \"Under Review\" to \"Interview Scheduled\" or \"Decision Made.\" You'll also receive email notifications when there are updates to your application."
    },
    {
      question: "What if I need to update my application?",
      answer: "You can update certain parts of your application after submission. To do this, log into your account, navigate to the application you wish to modify, and select \"Edit Application.\" You can update your contact information, resume, and other details. Note that once assessments are completed, they cannot be retaken unless specifically reset by an administrator."
    },
    {
      question: "How long does the application process take?",
      answer: "The initial application, including the assessment and video introduction, typically takes 30-45 minutes to complete. After submission, employers usually review applications within 1-2 weeks, though this timeline can vary depending on the position and hiring urgency. You'll receive updates on your application status throughout the process."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16 bg-apple-gray">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="mb-4 flex justify-center">
              <MessageCircleQuestion className="h-12 w-12 text-apple-blue" />
            </div>
            <h1 className="text-4xl font-bold text-apple-text mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-apple-text-secondary max-w-3xl mx-auto">
              Find answers to common questions about our platform
            </p>
          </div>

          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 mb-8">
            <FAQSection faqs={faqItems} />
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              <HelpCircle className="h-10 w-10 text-apple-blue mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-apple-text mb-3">
                Still have questions?
              </h3>
              <p className="text-apple-text-secondary mb-6">
                If you couldn't find the answer you're looking for, our support team is here to help.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/contact">
                  <Button className="w-full sm:w-auto">
                    Contact Support
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
